import React from 'react';
import { Col, Form, FormInstance, Row } from 'antd';
import dayjs from 'dayjs';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormSelectOrAddItem } from '../../components/forms/FormSelectOrAddItem';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { CandidateAttachements } from '../candidate-details/CandidateAttachements';
import { useUpdateCandidate } from '../candidate-details/useUpdateCandidate';
import { CandidateType } from '../candidates/Candidates.types';
import { SaveButtonIcon } from '../dictionaries/DictionaryItemsList';
import { useGetDictionaryByName } from '../dictionaries/useGetDictionaryByName';
import { AttachmentType } from '../emails/Emails.type';

import { SubmitButton } from './RecruitmentDataForm';

type TechnicalReviewProps = {
  candidate?: CandidateType;
  form?: FormInstance;
  standAloneMode?: boolean;
  technicalInterviewAttachements: AttachmentType[];
  attachFile: (fileId: string) => void;
};

export const TechnicalReview: React.FC<TechnicalReviewProps> = ({
  candidate,
  form: candidateForm,
  standAloneMode = false,
  technicalInterviewAttachements,
  attachFile,
}) => {
  const [newForm] = Form.useForm();
  const updateCandidate = useUpdateCandidate();

  const form = candidateForm ?? newForm;

  const { data: interviewAssessment, isLoading: interviewAssessmentLoading } =
    useGetDictionaryByName('interview-assessment');

  const interviewAssessmentOptions =
    interviewAssessment && !interviewAssessmentLoading
      ? interviewAssessment.map((assessment) => ({ value: assessment.value, content: assessment.value }))
      : [];

  const initialValues =
    standAloneMode && candidate
      ? {
          ...(candidate.technicalInterviewDate
            ? { technicalInterviewDate: dayjs.unix(candidate.technicalInterviewDate) }
            : []),
          technicalInterviewAssessment: candidate.technicalInterviewAssessment,
          technicalInterviewSummary: candidate.technicalInterviewSummary,
        }
      : {};

  const submitForm = () => {
    if (!candidate) {
      return;
    }
    const dataValues = form.getFieldsValue();
    updateCandidate.mutate(
      {
        queryParams: { id: candidate.uuid },
        data: {
          ...dataValues,
          technicalInterviewDate: dayjs(dataValues['technicalInterviewDate']).unix(),
        },
      },
      {
        onSuccess: () => clearFieldErrors(form),
        onError: (error) => {
          handleFormErrors({ error, form });
        },
      }
    );
  };

  return (
    <Form form={form} initialValues={initialValues} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <Row gutter={[10, 5]}>
          <Col xs={24} md={8}>
            <FormDatePicker name="technicalInterviewDate" label="Technical interview date" />
          </Col>
          <Col xs={24} md={16}>
            <FormSelectOrAddItem
              name="technicalInterviewAssessment"
              label="Technical score / assessment"
              form={form}
              options={interviewAssessmentOptions}
            />
          </Col>
        </Row>
        <CandidateAttachements attachments={technicalInterviewAttachements} handleAttachmentUpload={attachFile} />
        <FormTextArea rows={19} name="technicalInterviewSummary" label="Technical interview description" />
        {standAloneMode && (
          <StyledRow justifyContent="flex-end" marginTop="14px">
            <SubmitButton onClick={submitForm} icon={<SaveButtonIcon />}>
              Save
            </SubmitButton>
          </StyledRow>
        )}
      </StyledColumn>
    </Form>
  );
};
